import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useMessages } from '../../hooks/useMessages';
import GifPicker from './GifPicker';
import { User } from '../../types';
import { auth } from '../../services/firebase';

interface MessageWidgetProps {
  isVisible: boolean;
  onToggle: () => void;
}

export const MessageWidget: React.FC<MessageWidgetProps> = ({ isVisible, onToggle }) => {
  const { user } = useAuth();
  const [isMinimized, setIsMinimized] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const {
    conversations,
    selectedConversation,
    messages,
    loading,
    error,
    sendMessage,
    selectConversation,
    startNewConversation
  } = useMessages();

  // Auto-scroll vers le bas
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !selectedImage) || !selectedConversation || isSending || !auth.currentUser) return;
    
    setIsSending(true);
    
    try {
      // Trouver l'autre participant (utiliser Firebase UID)
      const firebaseUid = auth.currentUser.uid;
      const otherParticipantId = selectedConversation.participants.find((id: any) => id !== firebaseUid);
      if (!otherParticipantId) return;
      
      let messageType: 'text' | 'image' | 'gif' = 'text';
      let messageContent = newMessage.trim();
      
      // Si une image est sélectionnée
      if (selectedImage) {
        messageType = selectedImage.includes('tenor.com') || selectedImage.includes('giphy.com') ? 'gif' : 'image';
        messageContent = selectedImage;
      }
      
      await sendMessage(otherParticipantId, messageContent, messageType);
      setNewMessage('');
      setSelectedImage(null);
      setShowEmojiPicker(false);
      setShowGifPicker(false);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleGifSelect = (gifUrl: string) => {
    setSelectedImage(gifUrl);
    setShowGifPicker(false);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeSelectedImage = () => {
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Auto-resize textarea
  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
    
    // Auto-resize
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 80) + 'px';
  };

  if (!isVisible || !user) return null;

  return (
    <div className={`fixed bottom-4 right-4 z-[9999] transition-all duration-300 ${
      isMinimized ? 'w-80 sm:w-96 h-14' : 'w-80 sm:w-96 h-96 max-h-[calc(100vh-2rem)]'
    }`}>
      {/* Widget Header */}
      <div className="cosmic-card border-b border-emerald-700/30 rounded-b-none">
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center space-x-2">
            <svg className="h-5 w-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <h3 className="text-sm font-medium text-emerald-200">Messages</h3>
            {messages.length > 0 && (
              <span className="bg-emerald-600 text-xs px-2 py-1 rounded-full">
                {messages.length}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1 hover:bg-emerald-800/30 rounded transition-colors"
              title={isMinimized ? "Agrandir" : "Réduire"}
            >
              <svg className={`h-4 w-4 transition-transform ${isMinimized ? 'rotate-180' : ''}`} 
                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <button
              onClick={onToggle}
              className="p-1 hover:bg-emerald-800/30 rounded text-emerald-400 transition-colors"
              title="Fermer"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Widget Content */}
      {!isMinimized && (
        <div className="cosmic-card border-t-0 rounded-t-none h-80 flex flex-col overflow-hidden relative z-[9999]">
          {/* Conversations List or Messages */}
          {!selectedConversation ? (
            <div className="flex-1 p-3 overflow-y-auto scrollbar-hide">
              <div className="text-center text-emerald-400 text-sm mb-4">
                Conversations récentes
              </div>
              {conversations.length === 0 ? (
                <div className="text-center text-gray-400 text-xs mt-8">
                  <svg className="h-12 w-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  Aucune conversation
                </div>
              ) : (
                <div className="space-y-2">
                  {conversations.slice(0, 5).map((conversation) => {
                    const firebaseUid = auth.currentUser?.uid;
                    const otherParticipantId = conversation.participants.find((id: any) => id !== firebaseUid);
                    return (
                      <div
                        key={conversation.id}
                        onClick={() => selectConversation(conversation.id)}
                        className="p-2 hover:bg-emerald-800/20 rounded cursor-pointer transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-xs font-medium">
                            {otherParticipantId?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-emerald-200 truncate">
                              {otherParticipantId || 'Utilisateur'}
                            </p>
                            <p className="text-xs text-gray-400 truncate">
                              {conversation.lastMessage?.content || 'Nouvelle conversation'}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Messages Header */}
              <div className="border-b border-emerald-700/30 p-2 flex items-center space-x-2 bg-emerald-950/40">
                <button
                  onClick={() => selectConversation('')}
                  className="p-1 hover:bg-emerald-800/30 rounded transition-colors"
                  title="Retour aux conversations"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center text-xs font-medium">
                  {(() => {
                    const firebaseUid = auth.currentUser?.uid;
                    const otherParticipantId = selectedConversation?.participants?.find((id: any) => id !== firebaseUid);
                    return otherParticipantId?.charAt(0)?.toUpperCase() || '?';
                  })()}
                </div>
                <span className="text-xs font-medium text-emerald-200 truncate flex-1">
                  {(() => {
                    const firebaseUid = auth.currentUser?.uid;
                    return selectedConversation?.participants?.find((id: any) => id !== firebaseUid) || 'Utilisateur';
                  })()}
                </span>
              </div>

              {/* Messages List */}
              <div className="flex-1 p-2 overflow-y-auto scrollbar-hide space-y-2">
                {messages.map((message) => {
                  const firebaseUid = auth.currentUser?.uid;
                  const isOwn = message.senderId === firebaseUid;
                  return (
                    <div
                      key={message.id}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[85%] ${isOwn ? 'text-right' : 'text-left'}`}>
                        <div
                          className={`inline-block px-3 py-2 rounded-lg text-xs break-words ${
                            isOwn
                              ? 'bg-emerald-600 text-white rounded-br-sm'
                              : 'bg-gray-700 text-gray-200 rounded-bl-sm'
                          }`}
                        >
                          {message.type === 'image' || message.type === 'gif' ? (
                            <img 
                              src={message.content} 
                              alt="Message media" 
                              className="max-w-full h-auto rounded"
                              style={{ maxHeight: '120px' }}
                              loading="lazy"
                            />
                          ) : (
                            <span className="whitespace-pre-wrap">{message.content}</span>
                          )}
                        </div>
                        <div className={`text-xs text-gray-500 mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                          {new Date(message.createdAt).toLocaleTimeString('fr-FR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                          {isOwn && message.isRead && (
                            <span className="ml-1 text-emerald-400">✓</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="border-t border-emerald-700/30 p-2">
                {/* Image Preview */}
                {selectedImage && (
                  <div className="mb-2 relative">
                    <img 
                      src={selectedImage} 
                      alt="Aperçu" 
                      className="max-h-24 sm:max-h-32 rounded border border-emerald-700/30"
                    />
                    <button
                      onClick={removeSelectedImage}
                      className="absolute top-1 right-1 bg-gray-900/80 hover:bg-gray-900 rounded-full p-1 transition-colors"
                      title="Supprimer l'image"
                    >
                      <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Input Container */}
                <div className="flex items-end space-x-2">
                  <div className="flex-1 bg-gray-800/50 border border-emerald-700/30 rounded-lg overflow-hidden">
                    <textarea
                      value={newMessage}
                      onChange={handleTextAreaChange}
                      onKeyPress={handleKeyPress}
                      placeholder="Écrivez votre message..."
                      className="w-full bg-transparent px-3 py-2 text-xs text-white placeholder-gray-400 focus:outline-none resize-none"
                      rows={1}
                      style={{ minHeight: '28px', maxHeight: '80px' }}
                    />
                    
                    {/* Media Controls */}
                    <div className="flex items-center justify-between px-3 pb-2">
                      <div className="flex items-center space-x-2">
                        {/* Image Upload */}
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="p-1 hover:bg-emerald-800/30 rounded-full text-cyan-400 hover:text-cyan-300 transition-colors"
                          title="Ajouter une image"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </button>

                        {/* GIF Picker */}
                        <button
                          onClick={() => setShowGifPicker(!showGifPicker)}
                          className="p-1 hover:bg-emerald-800/30 rounded-full text-cyan-400 hover:text-cyan-300 transition-colors"
                          title="Ajouter un GIF"
                        >
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19,10.5V8.8H17.3V16.6H19V15H20.7V13.4H19V10.5M15.7,13.9L14.7,9.6H13.3L12.2,13.9L11.1,9.6H9.6L11.1,16.6H12.6L13.8,12L15,16.6H16.5L18,9.6H16.5L15.7,13.9M9,9.6H5.8V16.6H7.5V14.8H9A2.4,2.4 0 0 0 11.4,12.4V11.8A2.4,2.4 0 0 0 9,9.4M9.1,13.3H7.5V11.1H9.1A1,1 0 0 1 10.1,12.2A1,1 0 0 1 9.1,13.3Z"/>
                          </svg>
                        </button>

                        {/* Emoji Picker */}
                        <button
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          className="p-1 hover:bg-emerald-800/30 rounded-full text-cyan-400 hover:text-cyan-300 transition-colors"
                          title="Ajouter un emoji"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      </div>

                      {/* Send Button */}
                      <button
                        onClick={handleSendMessage}
                        disabled={(!newMessage.trim() && !selectedImage) || isSending}
                        className="p-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-full transition-colors"
                        title="Envoyer le message"
                      >
                        {isSending ? (
                          <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Hidden File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />

                {/* Emoji Picker */}
                {showEmojiPicker && (
                  <div className="absolute bottom-full right-0 mb-2 bg-gray-900 border border-emerald-700/30 rounded-lg p-3 z-[10000] shadow-lg">
                    <div className="grid grid-cols-6 gap-2 text-lg">
                      {['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩'].map((emoji) => (
                        <button
                          key={emoji}
                          onClick={() => handleEmojiSelect(emoji)}
                          className="hover:bg-emerald-800/30 p-1 rounded transition-colors"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* GIF Picker */}
                {showGifPicker && (
                  <div className="absolute bottom-full right-0 mb-2 w-80 h-64 z-[10000]">
                    <GifPicker
                      isOpen={showGifPicker}
                      onSelectGif={handleGifSelect}
                      onClose={() => setShowGifPicker(false)}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
